import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Scope variant updates inside the cart drawer to update card images
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    // Hover preview for swatches inside resource cards in the drawer (upsell)
    this.addEventListener('pointerover', this.#handleSwatchPointerOver);
    this.addEventListener('pointerout', this.#handleSwatchPointerOut);
    // Fallback for browsers without pointer events
    this.addEventListener('mouseover', this.#handleSwatchPointerOver);
    this.addEventListener('mouseout', this.#handleSwatchPointerOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.removeEventListener('pointerover', this.#handleSwatchPointerOver);
    this.removeEventListener('pointerout', this.#handleSwatchPointerOut);
    this.removeEventListener('mouseover', this.#handleSwatchPointerOver);
    this.removeEventListener('mouseout', this.#handleSwatchPointerOut);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Update the resource-card image in the upsell list when a variant changes via swatches.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const picker = /** @type {HTMLElement|null} */ (event?.target instanceof HTMLElement ? event.target : null);
    if (!picker) return;

    // Only handle updates originating from a swatches/variant picker inside a resource card
    const card = picker.closest('.resource-card[data-product-id]');
    if (!card) return;

    const img = card.querySelector('.resource-card__image');
    if (!(img instanceof HTMLImageElement)) return;

    const variant = event.detail?.resource || null;
    const mediaSrc = variant?.featured_media?.preview_image?.src || '';
    const mediaId = variant?.featured_media?.id || '';

    // Determine target width from existing URL if present, else fallback to 500
    const currentUrl = img.currentSrc || img.src || '';
    const widthMatch = currentUrl.match(/(?:[?&])width=(\d+)/);
    const desiredWidth = widthMatch ? widthMatch[1] : '500';

    if (mediaSrc) {
      const join = mediaSrc.includes('?') ? '&' : '?';
      const next = `${mediaSrc}${join}width=${desiredWidth}`;
      img.src = next;
      img.srcset = next;
      if (mediaId) img.setAttribute('data-media-id', String(mediaId));
    } else {
      const fallback = img.getAttribute('data-default-src');
      if (fallback) {
        img.src = fallback;
        img.srcset = fallback;
        img.removeAttribute('data-media-id');
      }
    }
  };

  /** @type {HTMLElement|null} */
  #hoverLabel = null;

  /**
   * Delegated pointerover handler for swatch labels to preview the image.
   * @param {PointerEvent | MouseEvent} e
   */
  #handleSwatchPointerOver = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const label = t.closest('.variant-option__button-label');
    if (!label || !this.contains(label)) return;

    // Only act for labels inside a resource-card within the drawer upsell
    const card = label.closest('.resource-card[data-product-id]');
    if (!card) return;

    if (this.#hoverLabel === label) return;
    this.#hoverLabel = label;

    const img = card.querySelector('.resource-card__image');
    if (!(img instanceof HTMLImageElement)) return;

    // Preferred preview from dataset
    const preview = label.getAttribute('data-media-src') || label.querySelector('input')?.getAttribute('data-option-media-src');

    if (preview) {
      img.dataset.previewing = 'true';
      img.dataset.prevSrc = img.currentSrc || img.src || '';
      img.src = preview;
      img.srcset = preview;
    }
  };

  /**
   * Delegated pointerout handler to restore the image after hover leaving swatches.
   * @param {PointerEvent | MouseEvent} e
   */
  #handleSwatchPointerOut = (e) => {
    const from = e.target instanceof Element ? e.target.closest('.variant-option__button-label') : null;
    const to = e.relatedTarget instanceof Element ? e.relatedTarget.closest('.variant-option__button-label') : null;
    if (!from || to === from) return;

    const card = from.closest('.resource-card[data-product-id]');
    if (!card) return;
    const img = card.querySelector('.resource-card__image');
    if (!(img instanceof HTMLImageElement)) return;

    // If moving to another label, the over handler will set new preview
    const isMovingToAnotherLabel = to && card.contains(to);
    if (isMovingToAnotherLabel) return;

    // Restore to the currently selected variant preview if present, else fallback to default
    const checked = card.querySelector('input[type="radio"][name*="-swatch"]:checked');
    const selectedPreview = checked?.getAttribute('data-option-media-src') || '';
    if (selectedPreview) {
      img.src = selectedPreview;
      img.srcset = selectedPreview;
    } else {
      const fallback = img.getAttribute('data-default-src');
      if (fallback) {
        img.src = fallback;
        img.srcset = fallback;
      }
    }
    img.removeAttribute('data-previewing');
    this.#hoverLabel = null;
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
